<script setup>
import { computed, ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'
import SeoCopy from '@/Components/Public/SeoCopy.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import EmptyResults from '@/Components/Public/EmptyResults.vue'
import TrustRow from '@/Components/Public/TrustRow.vue'
import PrivateVsShared from '@/Components/Public/PrivateVsShared.vue'

// Products first (round-one item 3, confirmed by round two): hero, the trust
// row, one compact filter row of four selects, then the boats. The feature
// and tier chips live behind "More filters". Every state is a plain GET, so
// the catalog works without JS and every combination is a shareable URL.
defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  charters:       { type: Array,  default: () => [] },
  filters:        { type: Object, default: () => ({}) },
  vessel_types:   { type: Array,  default: () => [] },
  occasions:      { type: Array,  default: () => [] },
  formats:        { type: Array,  default: () => [] },
  capacity_steps: { type: Array,  default: () => [] },
  feature_groups: { type: Object, default: () => ({}) },
  base_path:      { type: String, default: '/charters' },
  plan_path:      { type: String, default: '/plan' },
  comparison:     { type: Object, default: null }
})

// Chip links keep the select values too (the base_path already carries any
// /_p + locale prefix, see Public::ChartersController).
function chipHref(overrides) {
  const q = { ...props.filters, ...overrides }
  const parts = Object.entries(q).filter(([, v]) => v != null && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
  return parts.length ? `${props.base_path}?${parts.join('&')}` : props.base_path
}
const anyFilter = computed(() => Object.values(props.filters || {}).some(v => v != null && v !== ''))
// Open the drawer when a chip inside it is the active filter, so the state
// a shared link carries is visible on arrival.
const moreOpen = ref(!!(props.filters.feature || props.filters.tier))

// A select change submits the form at once; without JS the button does.
// Empty selects are dropped from the URL (a disabled field is not posted),
// so a shared link carries only the choices that were made.
function onChange(e) {
  const form = e.target.closest('form')
  if (!form) return
  form.querySelectorAll('select').forEach(s => { if (!s.value) s.disabled = true })
  if (form.requestSubmit) form.requestSubmit()
  else form.submit()
}
</script>

<template>
  <Head>
    <title>{{ t('public.charters.head_title') }}</title>
    <meta name="description" :content="t('public.charters.head_description')">
  </Head>

  <BrandHero kind="charter" :title="t('public.charters.title')">
    <p class="bhero-lede">{{ t('public.charters.lede') }}</p>
  </BrandHero>

  <section class="listing">
    <div class="listing__inner">
      <div class="trust"><TrustRow /></div>

      <form class="fbar" method="get" :action="base_path" :aria-label="t('public.charters.filters.aria')">
        <label class="fbar__f">
          <span class="fbar__label">{{ t('public.charters.filters.occasion') }}</span>
          <select name="occasion" class="fbar__select" :value="filters.occasion || ''" @change="onChange">
            <option value="">{{ t('public.charters.filters.any') }}</option>
            <option v-for="o in occasions" :key="o" :value="o">{{ t(`public.charters.occasions.${o}`) }}</option>
          </select>
        </label>
        <label class="fbar__f">
          <span class="fbar__label">{{ t('public.charters.filters.vessel_type') }}</span>
          <select name="vessel_type" class="fbar__select" :value="filters.vessel_type || ''" @change="onChange">
            <option value="">{{ t('public.charters.filters.any') }}</option>
            <option v-for="vt in vessel_types" :key="vt" :value="vt">{{ t(`public.charters.vessel_types.${vt}`) }}</option>
          </select>
        </label>
        <label class="fbar__f">
          <span class="fbar__label">{{ t('public.charters.filters.trip') }}</span>
          <select name="trip" class="fbar__select" :value="filters.trip || ''" @change="onChange">
            <option value="">{{ t('public.charters.filters.any') }}</option>
            <option v-for="f in formats" :key="f" :value="f">{{ t(`public.charters.formats.${f}`) }}</option>
          </select>
        </label>
        <label class="fbar__f">
          <span class="fbar__label">{{ t('public.charters.filters.guests') }}</span>
          <select name="capacity" class="fbar__select" :value="filters.capacity || ''" @change="onChange">
            <option value="">{{ t('public.charters.filters.any') }}</option>
            <option v-for="n in capacity_steps" :key="n" :value="n">{{ t('public.charters.filters.guests_min', { n }) }}</option>
          </select>
        </label>
        <input v-if="filters.feature" type="hidden" name="feature" :value="filters.feature">
        <input v-if="filters.tier" type="hidden" name="tier" :value="filters.tier">
        <button type="submit" class="fbar__go">{{ t('public.charters.filters.show') }}</button>
        <button type="button" class="fbar__more" :aria-expanded="moreOpen ? 'true' : 'false'" aria-controls="more-filters" @click="moreOpen = !moreOpen">
          {{ moreOpen ? t('public.charters.filters.less') : t('public.charters.filters.more') }}
        </button>
      </form>

      <div v-show="moreOpen" id="more-filters" class="more">
        <nav class="chips" :aria-label="t('public.charters.filters.tier')">
          <span class="chips__label">{{ t('public.charters.filters.tier') }}</span>
          <a v-for="tier in ['premium', 'luxury']" :key="tier" :href="chipHref({ tier: filters.tier === tier ? null : tier })" class="chip" :class="{ 'chip--on': filters.tier === tier }">{{ t(`public.tiers.${tier}`) }}</a>
        </nav>
        <nav v-for="(keys, group) in feature_groups" :key="group" class="chips" :aria-label="t(`public.charters.features.groups.${group}`)">
          <span class="chips__label">{{ t(`public.charters.features.groups.${group}`) }}</span>
          <a v-for="f in keys" :key="f" :href="chipHref({ feature: filters.feature === f ? null : f })" class="chip" :class="{ 'chip--on': filters.feature === f }">{{ t(`public.charters.features.items.${f}`) }}</a>
        </nav>
      </div>

      <p class="count">
        <span>{{ t('public.charters.filters.count', { n: charters.length }) }}</span>
        <a v-if="anyFilter" :href="base_path" class="count__reset">{{ t('public.charters.filters.all') }}</a>
      </p>

      <EmptyResults
        v-if="charters.length === 0"
        :title="t('public.charters.empty_title')"
        :sub="t('public.charters.empty_sub')"
      />
      <ul v-else class="grid">
        <li v-for="c in charters" :key="c.id">
          <ProductCard kind="charter" :item="c" :href="`${base_path}/${c.id}`" />
        </li>
      </ul>

      <div v-if="comparison" class="compare"><PrivateVsShared :comparison="comparison" :charters_path="base_path" :plan_path="plan_path" /></div>

      <section class="promise">
        <div class="promise__inner">
          <p class="promise__title">{{ t('public.charters.route_promise_title') }}</p>
          <p class="promise__body">{{ t('public.charters.route_promise_body') }}</p>
        </div>
      </section>

      <div class="plan-band">
        <p>{{ t('public.charters.plan_band') }}</p>
        <LuxButton :href="plan_path" variant="solid" tone="light" glyph="arrow">{{ t('public.charter_home.hero.cta_plan') }}</LuxButton>
      </div>
    </div>
  </section>

  <SeoCopy section="charters" />
</template>

<style scoped>
.bhero-lede { margin: 1rem 0 0; font-size: clamp(1.05rem, 2vw, 1.3rem); line-height: 1.5; max-width: 52ch; opacity: 0.92; }

.listing { --card-accent: var(--second); background: var(--bg); }
.listing__inner { max-width: 96rem; margin: 0 auto; padding: clamp(1.1rem, 2.5vw, 1.75rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.trust { padding-bottom: clamp(0.9rem, 2vw, 1.25rem); margin-bottom: clamp(0.9rem, 2vw, 1.25rem); border-bottom: 1px solid color-mix(in oklch, var(--gold, var(--warn)) 40%, var(--line)); }

/* The filter row: four selects, a submit, a drawer toggle — one line on
   desktop, a 2×2 grid plus the two buttons on a phone. */
.fbar { display: grid; grid-template-columns: 1.2fr 1fr 1fr 0.9fr auto auto; gap: 0.6rem; align-items: end; }
.fbar__f { display: grid; gap: 0.3rem; min-width: 0; }
.fbar__label { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 700; }
.fbar__select {
  width: 100%; min-width: 0; padding: 0.6rem 2.2rem 0.6rem 0.8rem; font: inherit; font-size: 0.95rem; color: var(--ink);
  background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-input, 0.6rem);
  appearance: none; -webkit-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--muted) 50%), linear-gradient(135deg, var(--muted) 50%, transparent 50%);
  background-position: calc(100% - 1.05rem) 55%, calc(100% - 0.7rem) 55%; background-size: 0.35rem 0.35rem, 0.35rem 0.35rem; background-repeat: no-repeat;
}
.fbar__select:focus-visible { outline: 2px solid var(--second); outline-offset: 1px; }
.fbar__go, .fbar__more {
  padding: 0.62rem 1rem; font: inherit; font-size: 0.9rem; font-weight: 700; border-radius: 999px; cursor: pointer; white-space: nowrap;
}
.fbar__go { background: var(--night); color: var(--foam); border: 1px solid var(--night); }
.fbar__more { background: var(--surface); color: var(--ink); border: 1px solid var(--line); }
.fbar__more:hover { border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); }
@media (max-width: 48rem) {
  .fbar { grid-template-columns: 1fr 1fr; }
  .fbar__go, .fbar__more { padding-block: 0.55rem; }
}

.more { margin-top: 0.9rem; padding: 0.9rem 0 0; border-top: 1px dashed var(--line); }
.chips { display: flex; flex-wrap: wrap; gap: 0.45rem; margin-bottom: 0.6rem; align-items: center; }
.chips__label { font-size: 0.72rem; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); font-weight: 700; margin-right: 0.25rem; }
.chip {
  display: inline-flex; align-items: center; padding: 0.4rem 0.8rem; border-radius: 999px;
  background: var(--surface); border: 1px solid var(--line); color: var(--inkSoft);
  font-size: 0.8rem; font-weight: 600; text-decoration: none; transition: border-color 150ms ease;
}
.chip:hover { border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); color: var(--ink); }
.chip--on { background: var(--night); color: var(--foam); border-color: var(--night); box-shadow: inset 0 0 0 1px color-mix(in oklch, var(--gold) 45%, transparent); }

.count { display: flex; gap: 1rem; align-items: baseline; margin: 1rem 0 0.9rem; font-size: 0.85rem; color: var(--muted); }
.count__reset { color: var(--ink); font-weight: 600; }

.grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr)); gap: 1.25rem; }

/* Route promise — a private boat's day belongs to the guest. Sits after the
   fleet now; products first. */
.compare { padding: clamp(2rem, 4vw, 3rem) 0 0; }
.promise { padding: clamp(2rem, 4vw, 3rem) 0 0; }
.promise__inner { display: grid; grid-template-columns: minmax(0, 18rem) minmax(0, 1fr); gap: 0.5rem 2.5rem; align-items: baseline; padding-top: clamp(1.25rem, 3vw, 2rem); border-top: 1px solid color-mix(in oklch, var(--gold, var(--warn)) 40%, var(--line)); }
.promise__title { margin: 0; font-family: var(--display); font-weight: 600; font-size: 1.55rem; line-height: 1.15; color: var(--ink); }
.promise__body { margin: 0; color: var(--inkSoft); line-height: 1.55; max-width: 64ch; }
@media (max-width: 48rem) { .promise__inner { grid-template-columns: 1fr; } }

.plan-band {
  margin-top: 2rem; padding: 1.5rem 1.75rem; border-radius: 1.25rem;
  background: linear-gradient(160deg, var(--sand, var(--surfaceAlt)) 0%, var(--accentSoft) 100%);
  display: flex; flex-wrap: wrap; gap: 1rem; align-items: center; justify-content: space-between;
}
.plan-band p { margin: 0; color: var(--ink); font-family: var(--font-display, var(--display)); font-size: 1.35rem; line-height: 1.2; max-width: 36ch; }
</style>
